<script setup lang="ts">
import type { IconDetail } from '~~/shared/types/menu'
import { buildAllergenTree } from '~/shared/allergenHierarchy'
import type { AllergenGroupEntry, AllergenSingleEntry } from '~/shared/allergenHierarchy'

const props = defineProps<{
  icons: IconDetail[]
}>()

const { t } = useI18n()
const { styles } = useVariants('allergiesList')
const filter = useAllergenFilter()

const tree = computed(() => buildAllergenTree(props.icons))
const singles = computed(() => tree.value.filter((e): e is AllergenSingleEntry => e.type === 'single'))
const groups = computed(() => tree.value.filter((e): e is AllergenGroupEntry => e.type === 'group'))

function iconLabel(id: string): string {
  const key = `allergy.${id}`
  const val = t(key)
  return val === key ? id : val
}
</script>

<template>
  <section v-if="icons.length" :class="styles.base()">
    <h2 :class="styles.heading()">
      {{ t("allergies") }}
      <button :class="styles.filterLink()" @click="filter.openFilter()">
        {{ t("filterOpen") }}
      </button>
    </h2>

    <!-- Standalone allergens & traits -->
    <ul v-if="singles.length" :class="styles.list()">
      <li
        v-for="entry in singles"
        :key="entry.icon.id"
        :title="iconLabel(entry.icon.id)"
        :class="styles.item()"
      >
        <MenuIcon :icon="entry.icon.id" class="shrink-0" />
        {{ iconLabel(entry.icon.id) }}
      </li>
    </ul>

    <!-- Sub-allergens grouped under their parent heading -->
    <template v-for="group in groups" :key="group.parentId">
      <h3 :class="styles.groupHeading()">
        <MenuIcon :icon="group.parentId" class="shrink-0" />
        {{ iconLabel(group.parentId) }}
      </h3>
      <ul :class="styles.list()">
        <li
          v-for="child in group.children"
          :key="child.id"
          :title="iconLabel(child.id)"
          :class="styles.item()"
        >
          <MenuIcon :icon="child.id" class="shrink-0" />
          {{ iconLabel(child.id) }}
        </li>
      </ul>
    </template>
  </section>
</template>
